import { Link } from 'react-router-dom';
import { useReveal, useFloatBtn, useCounter } from '../components/hooks';
import Footer from '../components/Footer';
import './About.css';

const timeline = [
  { n: '01', year: 'Start', title: 'The Struggle', text: 'Grew up overweight and unhappy in my body. Years of low energy, low confidence, and feeling like change wasn\'t possible for someone like me.' },
  { n: '02', year: 'Year 1', title: 'The Decision', text: 'Made the decision to stop waiting for the "right time." Started training consistently for the first time — properly, with structure and real nutrition.', right: true },
  { n: '03', year: 'Year 3', title: 'The Transformation', text: 'Over 3–5 years of committed training and nutrition, completely rebuilt my body. Lost fat, built muscle, and gained the energy and confidence that changed my whole life.' },
  { n: '04', year: 'Year 4', title: 'First Clients', text: 'Started training friends and family informally. Watched them get results. Became addicted to helping others achieve what I\'d achieved myself.', right: true },
  { n: '05', year: 'Quals', title: 'Getting Qualified', text: 'Achieved Level 2 Gym Instructor, Level 3 Personal Trainer, and Level 4 Strength & Conditioning qualifications — all CIMSPA Endorsed and OFQUAL Regulated.' },
  { n: '06', year: 'Now', title: 'Liam W Coaching', text: 'Built a coaching business in Bath with one mission: to give every single client a bespoke plan, genuine accountability, and results that actually last. No copy-paste. No shortcuts.', right: true },
];

const values = [
  { icon: '🚫', n: '01', title: 'Zero Copy-Paste', text: 'Every single plan is written from scratch for you. Your body, your metabolism, your goals, your life. I don\'t own a library of templates and I never will.' },
  { icon: '❤️', n: '02', title: 'I Genuinely Care', text: 'Your results matter to me personally. Not because they\'re good for my business — but because I know how life-changing transformation actually feels.' },
  { icon: '🧠', n: '03', title: 'Mindset Is Everything', text: 'The body follows the mind. Consistency beats perfection. Progress over everything. I coach the mental side of transformation as much as the physical.' },
  { icon: '📊', n: '04', title: 'Science, Not Trends', text: 'Progressive overload. Body recomposition. Sustainable nutrition. I work with what the science actually says — not what\'s trending on social media this week.' },
  { icon: '🤝', n: '05', title: 'Honest Relationships', text: 'I\'m like a mate who also happens to be your coach. I\'ll be straight with you, celebrate your wins, and hold you accountable when you need it.' },
  { icon: '🏆', n: '06', title: 'Results, Always', text: 'Everything I do is in service of one outcome: your transformation. Not a quick fix. Not a 4-week programme. A real, lasting change in your body and your life.' },
];

const facts = [
  { icon: '🏠', title: 'Proud Homebody', text: 'When I\'m not coaching or training, you\'ll find me at home — cooking, listening to music, and recharging. I believe in balance, and rest is part of the process.' },
  { icon: '🍳', title: 'I Actually Cook', text: 'Nutrition isn\'t just a plan I write for clients — it\'s a lifestyle I live. I enjoy cooking real food and understanding how it fuels performance. That passion comes through in every nutrition plan.' },
  { icon: '🎵', title: 'Music Is My Fuel', text: 'Training without music is a crime. My playlists are carefully curated — different sessions, different energy levels, different genres. Music is part of the training experience for me.' },
  { icon: '📍', title: 'Bath Born & Based', text: 'Proud to be based in Bath, UK. I offer in-person sessions locally for clients who want face-to-face coaching, and deliver the full experience online for everyone else worldwide.' },
  { icon: '💪', title: 'I Train 5–6 Days a Week', text: 'I practise exactly what I preach. I train 5 to 6 days every single week — not to show off, but because I love it, and because I believe a coach should live the lifestyle they\'re asking their clients to adopt.' },
  { icon: '🔥', title: 'Most Proud Moment', text: 'Helping a client who had completely given up hope — someone who had tried everything and believed transformation simply wasn\'t possible for them. Watching them prove themselves wrong is something I\'ll never forget.' },
];

export default function About() {
  useReveal();
  useFloatBtn();
  useCounter('stats-strip');

  return (
    <>
      {/* HERO */}
      <section id="hero">
        <div className="hero-bg"></div>
        <div className="hero-grid"></div>
        <div className="hero-inner">
          <div className="hero-photo-wrap">
            <div className="hero-photo">
              <div className="hero-initials">LW</div>
            </div>
            <div className="hero-photo-badge">Level 4<br />Strength &amp; Conditioning<br />CIMSPA · OFQUAL</div>
            <div className="hero-photo-corner"></div>
            <div className="hero-photo-corner2"></div>
          </div>
          <div className="hero-text">
            <div className="hero-location">
              <div className="hero-location-dot"></div>
              Bath, UK · Available Worldwide Online
            </div>
            <h1 className="hero-name">LIAM<br /><em>WILLIAMSON</em></h1>
            <div className="hero-role">Personal Trainer · Strength Coach · Online Coach</div>
            <p className="hero-tagline">"I know what it's like to be in a body you're not proud of. I also know exactly what it takes to change it — for good."</p>
            <div className="hero-chips">
              {['Level 4 S&C Coach','CIMSPA Endorsed','OFQUAL Regulated','200+ Clients','Bath · Worldwide'].map(c => (
                <div className="chip" key={c}>{c}</div>
              ))}
            </div>
            <div className="hero-btns">
              <Link to="/packages" className="btn-gold">Work With Me →</Link>
              <a href="#story" className="btn-outline">My Story</a>
            </div>
          </div>
        </div>
      </section>

      {/* LETTER */}
      <section id="letter">
        <div className="letter-wrap">
          <div className="letter-opener rev">If you've tried before and failed — this is for you.</div>
          <div className="letter-body rev">
            <p>My name is Liam. I'm a Level 4 Strength &amp; Conditioning Coach based in Bath, UK — and I work with people who are serious about changing their body for good.</p>
            <p>I know what it's like to feel stuck. To try programme after programme and wonder why nothing works for you. I lived that frustration for years before I finally cracked the code — and now I've helped <strong>over 200 people</strong> do the same.</p>
            <p>I'm not going to give you a template. I'm not going to hand you a PDF and disappear. I'm going to build you a plan that's <strong>entirely yours</strong> — and I'm going to coach you through it, week by week, until you see results you're genuinely proud of.</p>
            <p>That's the promise. No asterisks. No exceptions.</p>
          </div>
          <div className="letter-sig rev">
            <div className="letter-sig-name">LIAM W <em>WILLIAMSON</em></div>
            <div className="letter-sig-role">Level 4 Strength &amp; Conditioning Coach · Liam W Coaching</div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <div id="stats-strip">
        {[{t:200,lbl:'Clients Transformed'},{t:100,lbl:'Satisfaction Rate'},{t:5,lbl:'Years Coaching'},{t:50,lbl:'Countries Worldwide'}].map(({ t, lbl }) => (
          <div className="ss-item" key={lbl}>
            <div className="ss-num" data-t={t}>0{t===100?'%':'+'}</div>
            <div className="ss-lbl">{lbl}</div>
          </div>
        ))}
      </div>

      {/* STORY */}
      <section id="story">
        <div className="story-text rev-l">
          <div className="s-label">Origin Story</div>
          <h2 className="s-title">I WAS YOU<br />ONCE.</h2>
          <p>I didn't grow up athletic. I wasn't the kid who was naturally lean or naturally strong. I was overweight, low on energy, and genuinely didn't believe my body could ever look or feel different.</p>
          <p>It took years of trying, failing, learning, and eventually <strong>figuring it out</strong> — and when I did, it changed everything. Not just how I looked, but how I felt, how I carried myself, how I showed up in every area of my life.</p>
          <p>That experience is the reason I became a coach. Because I want to give every single client what I wish someone had given me: a real plan, real accountability, and a coach who actually gives a damn.</p>
          <div className="story-pull">"When your body changes, everything changes. That's not a cliché — I've watched it happen with 200+ clients."</div>
        </div>
        <div className="story-visual rev-r">
          <div className="story-img">
            <div className="story-initials-sm">LW</div>
          </div>
          <div className="story-tag">Bath, UK<br />Est. 2020<br />200+ Transformed</div>
        </div>
      </section>

      {/* TIMELINE */}
      <section id="timeline">
        <div className="timeline-header">
          <div className="s-label rev"><span>The Journey</span></div>
          <h2 className="s-title rev">HOW I GOT<br />TO WHERE<br />I AM TODAY</h2>
        </div>
        <div className="tl-wrap">
          <div className="tl-line"></div>
          <div className="tl-items">
            {timeline.map((item) => (
              <div className="tl-item rev" key={item.n}>
                {item.right ? (
                  <>
                    <div className="tl-empty"></div>
                    <div className="tl-mid"><div className="tl-dot">{item.n}</div><div className="tl-year">{item.year}</div></div>
                    <div className="tl-content-right"><div className="tl-card"><h3>{item.title}</h3><p>{item.text}</p></div></div>
                  </>
                ) : (
                  <>
                    <div className="tl-content-left"><div className="tl-card"><h3>{item.title}</h3><p>{item.text}</p></div></div>
                    <div className="tl-mid"><div className="tl-dot">{item.n}</div><div className="tl-year">{item.year}</div></div>
                    <div className="tl-empty2"></div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section id="values">
        <div className="values-header">
          <div className="s-label rev"><span>What I Stand For</span></div>
          <h2 className="s-title rev">MY VALUES<br />AS A COACH</h2>
        </div>
        <div className="values-grid">
          {values.map((v, i) => (
            <div className="value-card rev" key={v.n} style={{ transitionDelay: `${i * 0.08}s` }}>
              <span className="value-icon">{v.icon}</span>
              <div className="value-num">{v.n}</div>
              <h3>{v.title}</h3>
              <p>{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FUN FACTS */}
      <section id="funfacts">
        <div className="ff-header">
          <div className="s-label rev"><span>The Personal Side</span></div>
          <h2 className="s-title rev">BEYOND<br />THE GYM</h2>
        </div>
        <div className="ff-grid">
          {facts.map((f, i) => (
            <div className="ff-card rev" key={f.title} style={{ transitionDelay: `${i * 0.08}s` }}>
              <div className="ff-icon">{f.icon}</div>
              <div className="ff-text">
                <h4>{f.title}</h4>
                <p>{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* QUALIFICATIONS */}
      <section id="quals">
        <div className="quals-inner">
          <div className="quals-left">
            <div className="s-label rev"><span>Credentials</span></div>
            <h2 className="s-title rev">QUALIFIED<br />TO THE<br />HIGHEST<br />STANDARD</h2>
            <p className="rev">Every qualification is CIMSPA Endorsed and OFQUAL Regulated — the gold standard of personal training certification in the UK. Not online certificates. Not weekend courses. The real thing.</p>
            <ul className="quals-list rev">
              {[
                { icon: '🎓', title: 'Gym Instructor', sub: 'CIMSPA Endorsed · OFQUAL Regulated', level: 'L2' },
                { icon: '🏋️', title: 'Personal Trainer', sub: 'CIMSPA Endorsed · OFQUAL Regulated', level: 'L3' },
                { icon: '⚡', title: 'Strength & Conditioning Coach', sub: 'CIMSPA Endorsed · OFQUAL Regulated', level: 'L4' },
              ].map(q => (
                <li className="quals-item" key={q.title}>
                  <div className="quals-item-icon">{q.icon}</div>
                  <div className="quals-item-text">
                    <div className="quals-item-title">{q.title}</div>
                    <div className="quals-item-sub">{q.sub}</div>
                  </div>
                  <div className="quals-item-level">{q.level}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="quals-visual rev-r">
            <div className="badge-ring">
              <div className="badge-ring-inner">
                <div className="badge-center">
                  <div className="badge-lv">LEVEL</div>
                  <div className="badge-num">4</div>
                  <div className="badge-desc">Strength &amp; Conditioning</div>
                </div>
              </div>
              <div className="badge-ring-text">
                <svg viewBox="0 0 200 200">
                  <defs><path id="circle-path" d="M 100,100 m -75,0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0" /></defs>
                  <text fontFamily="'Barlow Condensed',sans-serif" fontSize="11" fill="rgba(201,169,110,0.5)" fontWeight="700" letterSpacing="4">
                    <textPath href="#circle-path">CIMSPA ENDORSED · OFQUAL REGULATED · UK CERTIFIED · </textPath>
                  </text>
                </svg>
              </div>
            </div>
            <div className="quals-trust">
              {['CIMSPA Endorsed Organisation','OFQUAL Regulated Qualifications','Highest Level UK Certification','Bath, UK · Available Worldwide'].map(t => (
                <div className="trust-pill" key={t}><span className="trust-pill-dot"></span>{t}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="cta">
        <div className="cta-bg"></div>
        <div className="cta-inner">
          <div className="urgency rev"><div className="udot"></div><span>Limited spots — new intake opens monthly</span></div>
          <h2 className="cta-quote rev">
            YOU OWE IT<br />TO YOURSELF<br />TO FIND OUT WHAT<br /><em>YOUR BODY</em><br />IS CAPABLE OF.
          </h2>
          <p className="cta-sub rev">Your body can do extraordinary things when it has the right plan, the right coach, and the right support. The only question is — are you ready to commit?</p>
          <div className="cta-btns rev">
            <Link to="/packages" className="btn-gold">Apply Now — It's Free</Link>
            <Link to="/packages" className="btn-outline">See the Packages →</Link>
          </div>
        </div>
      </section>

      <Footer />
      <Link to="/packages" className="float-btn" id="floatBtn">Apply Now →</Link>
    </>
  );
}
